import React, { useCallback, useRef, useState } from 'react'
import moment from 'moment'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faAnglesLeft,
  faAnglesRight,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faFilter,
  faMagnifyingGlass,
  faPlus,
  faRefresh,
} from '@fortawesome/pro-regular-svg-icons'
import {
  Box,
  Collapse,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  outlinedInputClasses,
} from '@mui/material'
import { DesktopDatePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { format, isValid } from 'date-fns'
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import Button from 'src/components/era-package/forms/Button'
import ChildCard from 'src/components/era-package/shared/ChildCard'
import BlankCard from 'src/components/era-package/shared/BlankCard'
import SnackBar from 'src/components/era-package/era/SnackBar'
import { useServerPagination } from 'src/views/abi/hooks/useServerPagination'
import AbiMidApiUrls from 'src/apiUrls/AbiMidApiUrls'
import { FileForUserGetCurrentUserId } from 'src/common/FileForUserCommon'
import { COUNTRY_OPTIONS } from 'src/views/uaempci/Common/constants/country'
import { ManufacturerMidDialog } from './ManufacturerMidDialog'
import { CANADA_STATE_CODES } from './canadaStateCodes'
import { ResponseSection } from './ResponseSection'

const stripCodePrefix = (text) => text.replace(/^\S+\s-\s/, '')

const COUNTRY_NAME_BY_CODE = Object.fromEntries(
  COUNTRY_OPTIONS.map((option) => [option.value, stripCodePrefix(option.text)])
)
const CANADA_STATE_NAME_BY_CODE = Object.fromEntries(
  CANADA_STATE_CODES.map((option) => [option.value, stripCodePrefix(option.text)])
)

const DEFAULT_PARAMS = {
  page: 0,
  size: 10,
  search: '',
  submittedStartDate: null,
  submittedEndDate: null,
  archived: 'N',
}

const columnHelper = createColumnHelper()

const formatDateTime = (value) => (value ? moment(new Date(value)).format('LL, h:mm A') : '-')

export const ManufacturerMidTab = () => {
  const { data, totalElements, totalPages, loading, params, fetchPage, updateParams } =
    useServerPagination(AbiMidApiUrls.LIST, DEFAULT_PARAMS)

  const [searchInput, setSearchInput] = useState('')
  const [filtersOpen, setFiltersOpen] = useState(false)
  const debounceRef = useRef(null)

  const [expandedIds, setExpandedIds] = useState(() => new Set())
  const [responseCache, setResponseCache] = useState({})
  const requestedRef = useRef(new Set())

  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState('create')
  const [dialogValue, setDialogValue] = useState(null)

  const [errorMessage, setErrorMessage] = useState('')
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false)

  const showApiError = useCallback((message) => {
    setErrorMessage(message || 'Internal error occurred, please try again later.')
    setShowErrorSnackbar(true)
  }, [])

  const showApiSuccess = useCallback((message) => {
    setSuccessMessage(message)
    setShowSuccessSnackbar(true)
  }, [])

  const openCreateDialog = useCallback(() => {
    setDialogMode('create')
    setDialogValue(null)
    setDialogOpen(true)
  }, [])

  const openEditDialog = useCallback((row) => {
    setDialogMode('edit')
    setDialogValue(row)
    setDialogOpen(true)
  }, [])

  const closeDialog = useCallback(() => setDialogOpen(false), [])

  const handleSave = useCallback(async (draft) => {
    const isCreateSave = draft.abiTransId == null
    const body = {
      name: draft.name,
      manufacturerId: draft.manufacturerId || null,
      country: draft.country,
      state: draft.country === 'CA' ? draft.state : null,
      street: draft.street,
      city: draft.city,
      postalCode: draft.postalCode,
      userId: FileForUserGetCurrentUserId(),
    }
    if (!isCreateSave) {
      body.abiTransId = draft.abiTransId
      body.actionCode = draft.actionCode
    }

    try {
      const response = await fetch(isCreateSave ? AbiMidApiUrls.CREATE : AbiMidApiUrls.UPDATE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('userToken')),
        },
        body: JSON.stringify(body),
      })
      const json = await response.json().catch(() => null)

      if (!response.ok) {
        showApiError(json?.message)
        return
      }

      setDialogOpen(false)
      showApiSuccess(isCreateSave ? 'Manufacturer added.' : 'Manufacturer updated.')
      fetchPage()
    } catch (error) {
      showApiError()
    }
  }, [fetchPage, showApiError, showApiSuccess])

  const ensureResponseLoaded = useCallback((abiTransId) => {
    if (requestedRef.current.has(abiTransId)) return
    requestedRef.current.add(abiTransId)

    setResponseCache((prev) => ({ ...prev, [abiTransId]: { loading: true } }))

    fetch(`${AbiMidApiUrls.STATUS_GET}?abiTransId=${abiTransId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('userToken')),
      },
    })
      .then((res) => res.json())
      .then((json) => {
        setResponseCache((prev) => ({ ...prev, [abiTransId]: { loading: false, data: json?.data ?? [] } }))
      })
      .catch(() => {
        setResponseCache((prev) => ({ ...prev, [abiTransId]: { loading: false, error: true } }))
        requestedRef.current.delete(abiTransId)
      })
  }, [])

  const toggleExpand = useCallback((abiTransId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(abiTransId)) {
        next.delete(abiTransId)
      } else {
        next.add(abiTransId)
      }
      return next
    })
  }, [])

  const handleReset = useCallback(() => {
    updateParams({ ...DEFAULT_PARAMS, page: 0 })
  }, [updateParams])

  const columns = React.useMemo(() => [
    columnHelper.display({
      id: 'expand',
      header: () => '',
      cell: (info) => (
        <IconButton
          size='small'
          onClick={(event) => {
            event.stopPropagation()
            toggleExpand(info.row.original.abiTransId)
          }}
        >
          <FontAwesomeIcon
            icon={faChevronDown}
            rotation={expandedIds.has(info.row.original.abiTransId) ? 180 : 0}
          />
        </IconButton>
      ),
    }),
    columnHelper.accessor('name', { header: () => 'Name', cell: (info) => info.getValue() ?? '-' }),
    columnHelper.accessor('manufacturerId', { header: () => 'Manufacturer ID', cell: (info) => info.getValue() ?? '-' }),
    columnHelper.accessor('country', { header: () => 'Country', cell: (info) => COUNTRY_NAME_BY_CODE[info.getValue()] ?? info.getValue() ?? '-' }),
    columnHelper.accessor('state', { header: () => 'State', cell: (info) => CANADA_STATE_NAME_BY_CODE[info.getValue()] ?? info.getValue() ?? '-' }),
    columnHelper.accessor('city', { header: () => 'City', cell: (info) => info.getValue() ?? '-' }),
    columnHelper.accessor('status', { header: () => 'Status', cell: (info) => info.getValue() ?? '-' }),
    columnHelper.accessor('userId', { header: () => 'User ID', cell: (info) => info.getValue() ?? '-' }),
    columnHelper.accessor('submittedTime', { header: () => 'Submitted Time', cell: (info) => formatDateTime(info.getValue()) }),
    columnHelper.accessor('responseTime', { header: () => 'Response Time', cell: (info) => formatDateTime(info.getValue()) }),
  ], [expandedIds, toggleExpand])

  const pageIndex = params.page
  const pageSize = params.size

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems='center' spacing={2} sx={{ mb: 2 }}>
          <TextField
            size='small'
            placeholder='Search all columns...'
            value={searchInput}
            onChange={(event) => {
              const value = event.target.value
              setSearchInput(value)
              clearTimeout(debounceRef.current)
              debounceRef.current = setTimeout(() => {
                updateParams({ search: value, page: 0 })
              }, 500)
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <FontAwesomeIcon icon={faMagnifyingGlass} style={{ width: 16, height: 16 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              maxWidth: { xs: '100%', sm: '300px' },
              [`& .${outlinedInputClasses.notchedOutline}`]: { inset: 0 },
            }}
          />

          <Button
            variant='outlined'
            onClick={() => setFiltersOpen((prev) => !prev)}
            startIcon={<FontAwesomeIcon icon={faFilter} style={{ width: 16, height: 16 }} />}
          >
            {filtersOpen ? 'Hide Filters' : 'Show Filters'}
          </Button>

          <Button variant='outlined' onClick={handleReset}>
            Reset Filters
          </Button>

          <Box flexGrow={1} />

          <Button
            variant='outlined'
            onClick={openCreateDialog}
            startIcon={<FontAwesomeIcon icon={faPlus} style={{ width: 16, height: 16 }} />}
          >
            Add
          </Button>
        </Stack>

        <Collapse in={filtersOpen} unmountOnExit>
          <BlankCard sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems='center'>
              <DesktopDatePicker
                label='Submitted Start Date'
                inputFormat='MM-dd-yyyy'
                value={params.submittedStartDate ? new Date(params.submittedStartDate) : null}
                onChange={(date) => {
                  if (date && !isValid(date)) return
                  updateParams({ submittedStartDate: date ? format(date, 'yyyy-MM-dd') : null, page: 0 })
                }}
                renderInput={(inputParams) => <TextField {...inputParams} size='small' />}
              />
              <DesktopDatePicker
                label='Submitted End Date'
                inputFormat='MM-dd-yyyy'
                value={params.submittedEndDate ? new Date(params.submittedEndDate) : null}
                onChange={(date) => {
                  if (date && !isValid(date)) return
                  updateParams({ submittedEndDate: date ? format(date, 'yyyy-MM-dd') : null, page: 0 })
                }}
                renderInput={(inputParams) => <TextField {...inputParams} size='small' />}
              />
              <Stack direction='row' alignItems='center' spacing={1}>
                <Typography>Archived</Typography>
                <Switch
                  checked={params.archived === 'Y'}
                  onChange={(event) => updateParams({ archived: event.target.checked ? 'Y' : 'N', page: 0 })}
                />
                <Typography>{params.archived === 'Y' ? 'Yes' : 'No'}</Typography>
              </Stack>
            </Stack>
          </BlankCard>
        </Collapse>

        <ChildCard sx={{ p: 0, mb: -3 }}>
          <Stack>
            <TableContainer>
              <Table>
                <TableHead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableCell key={header.id}>
                          <Typography variant='body' color='primary.main' fontWeight='600'>
                            {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                          </Typography>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableHead>
                <TableBody>
                  {loading && (
                    <TableRow>
                      <TableCell colSpan={columns.length} align='center'>
                        <Typography variant='body2' color='text.secondary'>Loading...</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && data.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={columns.length} align='center'>
                        <Typography variant='body2' color='text.secondary'>No manufacturer data available</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {table.getRowModel().rows.map((row) => {
                    const isExpanded = expandedIds.has(row.original.abiTransId)
                    return (
                      <React.Fragment key={row.id}>
                        <TableRow
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => openEditDialog(row.original)}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                          ))}
                        </TableRow>
                        {isExpanded && (
                          <TableRow>
                            <TableCell colSpan={columns.length} sx={{ backgroundColor: 'action.hover' }}>
                              <ResponseSection
                                record={row.original}
                                cache={responseCache}
                                ensureLoaded={ensureResponseLoaded}
                              />
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack
              gap={1}
              p={3}
              alignItems='center'
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent='space-between'
            >
              <Box display='flex' alignItems='center' gap={1}>
                <Button
                  variant='contained'
                  color='primary'
                  onClick={() => fetchPage()}
                  startIcon={<FontAwesomeIcon icon={faRefresh} style={{ width: 16, height: 16 }} spin={loading} />}
                >
                  Refresh data
                </Button>
                <Typography variant='body1'>{totalElements} Rows</Typography>
              </Box>

              <Box sx={{ display: { xs: 'block', sm: 'flex' } }} alignItems='center' gap={1}>
                <Stack direction='row' alignItems='center' gap={1}>
                  <Typography variant='body1'>Page</Typography>
                  <Typography variant='body1' fontWeight={600}>{pageIndex + 1} of {Math.max(totalPages, 1)}</Typography>
                </Stack>
                <Stack direction='row' alignItems='center' gap={1}>
                  | Go to page:
                  <TextField
                    type='number'
                    inputProps={{ min: 1, max: totalPages }}
                    defaultValue={pageIndex + 1}
                    onChange={(event) => {
                      const val = Number(event.target.value)
                      if (!val || val < 1) return
                      updateParams({ page: Math.min(val, totalPages) - 1 })
                    }}
                    size='small'
                  />
                </Stack>
                <Select
                  value={pageSize}
                  onChange={(event) => updateParams({ size: Number(event.target.value), page: 0 })}
                  size='small'
                >
                  {[10, 20, 25, 50].map((size) => (
                    <MenuItem key={size} value={size}>{size}</MenuItem>
                  ))}
                </Select>

                <IconButton size='small' onClick={() => updateParams({ page: 0 })} disabled={pageIndex === 0}>
                  <FontAwesomeIcon icon={faAnglesLeft} />
                </IconButton>
                <IconButton size='small' onClick={() => updateParams({ page: pageIndex - 1 })} disabled={pageIndex === 0}>
                  <FontAwesomeIcon icon={faChevronLeft} />
                </IconButton>
                <IconButton size='small' onClick={() => updateParams({ page: pageIndex + 1 })} disabled={pageIndex >= totalPages - 1}>
                  <FontAwesomeIcon icon={faChevronRight} />
                </IconButton>
                <IconButton size='small' onClick={() => updateParams({ page: totalPages - 1 })} disabled={pageIndex >= totalPages - 1}>
                  <FontAwesomeIcon icon={faAnglesRight} />
                </IconButton>
              </Box>
            </Stack>
          </Stack>
        </ChildCard>
      </Stack>

      <ManufacturerMidDialog
        open={dialogOpen}
        mode={dialogMode}
        initialValue={dialogValue}
        onClose={closeDialog}
        onSave={handleSave}
      />

      <SnackBar title='Error' type='error' open={showErrorSnackbar} onClose={() => setShowErrorSnackbar(false)} duration={10000}>
        {errorMessage || 'Internal error occurred, please try again later.'}
      </SnackBar>

      <SnackBar title='Success' type='success' open={showSuccessSnackbar} onClose={() => setShowSuccessSnackbar(false)} duration={4000}>
        {successMessage}
      </SnackBar>
    </LocalizationProvider>
  )
}

export default ManufacturerMidTab
